import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'

interface User {
  uid: string
  email: string | null
  display_name: string | null
  role: string
  subscription_expires_at: string | null
  created_at: string | null
  updated_at: string | null
}

export default function UsersPage() {
  const { api } = useApi()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [updating, setUpdating] = useState<string | null>(null)
  
  const fetchUsers = async () => {
    try {
      const params: Record<string, string> = {}
      if (roleFilter) params.role_filter = roleFilter
      const res = await api.get('/auth/users', { params })
      setUsers(res.data)
      setError(null)
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => { fetchUsers() }, [])
  
  const handleRoleChange = async (uid: string, newRole: string) => {
    setUpdating(uid)
    try {
      await api.put(`/auth/users/${uid}/role?new_role=${newRole}`)
      fetchUsers()
    } catch {
      setError('Failed to update role')
    } finally {
      setUpdating(null)
    }
  }
  
  const filtered = users.filter(u =>
    (u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.uid.includes(searchTerm))
  )
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }
  
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">User Management</h1>
      
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        />
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">All Roles</option>
          <option value="normal">Normal</option>
          <option value="pro">Pro</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      
      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      
      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map(user => (
              <tr key={user.uid} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm font-medium">
                        {user.display_name?.[0]?.toUpperCase() || user.uid[0]?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {user.display_name || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.email || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'pro' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {user.subscription_expires_at
                    ? new Date(user.subscription_expires_at).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-6 py-4">
                  {user.role !== 'admin' && (
                    <select
                      value={user.role}
                      onChange={e => handleRoleChange(user.uid, e.target.value)}
                      disabled={updating === user.uid}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                    >
                      <option value="normal">Normal</option>
                      <option value="pro">Pro</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500">No users found</div>
        )}
      </div>
    </div>
  )
}
